import { gridDateComparator } from '@mui/x-data-grid-pro';
import { CuriosityNamespaceType } from '@procureai/curiosity-ui-mui';
import { DateFormatPreset } from '@procureai/ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { I18nNamespace } from '~/common/types';
import {
  CuriosityAutocompleteCell,
  DataGridConfig,
  DateEditCell,
  dateFormatterFactory,
  DeleteActionCell,
  editCellFactory,
  gridColDefFactory,
  gridColumnGroupFactory,
  headerGroupWithIcon,
  UseDataEditGridConfig,
} from '~/components/DataGridCustom';
import { CurrencyCell } from '~/components/DataGridCustom/editCells/CurrencyCell/CurrencyCell';
import { DecimalTextFieldCell } from '~/components/DataGridCustom/editCells/DecimalTextFieldCell/DecimalTextFieldCell';
import { TextFieldCell } from '~/components/DataGridCustom/editCells/TextFieldCell/TextFieldCell';
import { IconId } from '~/components/Icon';

import { QuoteLineItemGridColumnGroup, QuoteLineItemGridField, QuoteLineItemGridRow } from './QuoteLineItemForm.types';

const pathPrefix = ['lineItems'];

export const useQuoteLineItemGridConfig: UseDataEditGridConfig<QuoteLineItemGridRow> = ({
  getRowIndex,
  onRowDelete,
  disabled,
}) => {
  const { t } = useTranslation();
  const { t: tColumn } = useTranslation(I18nNamespace.common, {
    keyPrefix: 'QuoteLineItemGrid.column',
  });
  const { t: tGroup } = useTranslation(I18nNamespace.common, {
    keyPrefix: 'QuoteLineItemGrid.group',
  });

  return useMemo<DataGridConfig<QuoteLineItemGridRow>>(() => {
    const standardCellConfig = {
      pathPrefix,
      getRowIndex,
      languageNamespace: I18nNamespace.common,
      keyPrefix: 'QuoteLineItemGrid.column',
      disabled,
    };

    const createColumn = gridColDefFactory({ tColumn, t, defaultOptions: { sortable: false } });
    const createGroup = gridColumnGroupFactory({ tGroup });

    return {
      sortModel: [],
      columns: [
        createColumn({
          field: QuoteLineItemGridField.POSITION,
          minWidth: 100,
          hideable: false,
          resizable: false,
          renderCell: editCellFactory(TextFieldCell, standardCellConfig),
        }),
        createColumn({
          field: QuoteLineItemGridField.TITLE,
          minWidth: 300,
          renderCell: editCellFactory(TextFieldCell, standardCellConfig),
        }),
        createColumn({
          field: QuoteLineItemGridField.DESCRIPTION,
          minWidth: 300,
          renderCell: editCellFactory(TextFieldCell, standardCellConfig),
        }),
        createColumn({
          field: QuoteLineItemGridField.COMMODITY_GROUP_ID,
          minWidth: 150,
          renderCell: editCellFactory(CuriosityAutocompleteCell, {
            ...standardCellConfig,
            ns: CuriosityNamespaceType.COMMODITY_GROUP,
          }),
        }),
        createColumn({
          field: QuoteLineItemGridField.UNIT_ID,
          minWidth: 150,
          renderCell: editCellFactory(CuriosityAutocompleteCell, {
            ...standardCellConfig,
            ns: CuriosityNamespaceType.UNIT,
          }),
        }),
        createColumn({
          field: QuoteLineItemGridField.QUANTITY,
          minWidth: 150,
          valueGetter: (_value, row) => {
            return row.quantity;
          },
          renderCell: editCellFactory(DecimalTextFieldCell, {
            ...standardCellConfig,
            decimalScale: 2,
          }),
        }),
        createColumn({
          field: QuoteLineItemGridField.PRICE_PER_UNIT,
          minWidth: 150,
          valueGetter: (_value, row) => {
            return row.pricePerUnit;
          },
          renderCell: editCellFactory(CurrencyCell, {
            ...standardCellConfig,
            decimalScale: 2,
          }),
        }),
        createColumn({
          field: QuoteLineItemGridField.EARLY_DELIVERY_AT,
          valueGetter: (_value, row) => {
            return row.earlyDeliveryAt;
          },
          valueFormatter: dateFormatterFactory({ preset: DateFormatPreset.TWO_DIGIT }),
          sortComparator: gridDateComparator,
          minWidth: 400,
          renderCell: editCellFactory(DateEditCell, standardCellConfig),
        }),
        createColumn({
          field: QuoteLineItemGridField.LATE_DELIVERY_AT,
          valueGetter: (_value, row) => {
            return row.lateDeliveryAt;
          },
          valueFormatter: dateFormatterFactory({ preset: DateFormatPreset.TWO_DIGIT }),
          sortComparator: gridDateComparator,
          minWidth: 400,
          renderCell: editCellFactory(DateEditCell, standardCellConfig),
        }),
        createColumn({
          field: QuoteLineItemGridField.DELETE_ACTION,
          minWidth: 72,
          width: 72,
          renderHeader: () => null,
          align: 'left',
          renderCell: editCellFactory(DeleteActionCell, {
            ...standardCellConfig,
            pathPrefix,
            onClick: onRowDelete,
          }),
        }),
      ],
      columnGroupingModel: [
        createGroup({
          groupId: QuoteLineItemGridColumnGroup.GENERAL,
          renderHeaderGroup: headerGroupWithIcon(IconId.demandRequestItem),
          children: [
            //
            { field: QuoteLineItemGridField.POSITION },
            { field: QuoteLineItemGridField.TITLE },
            { field: QuoteLineItemGridField.DESCRIPTION },
            { field: QuoteLineItemGridField.COMMODITY_GROUP_ID },
            { field: QuoteLineItemGridField.UNIT_ID },
            { field: QuoteLineItemGridField.QUANTITY },
            { field: QuoteLineItemGridField.PRICE_PER_UNIT },
          ],
        }),
        createGroup({
          groupId: QuoteLineItemGridColumnGroup.DELIVERY_DATE,
          renderHeaderGroup: headerGroupWithIcon(IconId.deliveryDate),
          children: [
            { field: QuoteLineItemGridField.EARLY_DELIVERY_AT },
            { field: QuoteLineItemGridField.LATE_DELIVERY_AT },
          ],
        }),
        createGroup({
          groupId: QuoteLineItemGridColumnGroup.ACTIONS,
          headerName: '',
          children: [
            //
            { field: QuoteLineItemGridField.DELETE_ACTION },
          ],
        }),
      ],
    };
  }, [getRowIndex, onRowDelete, t, tColumn, tGroup, disabled]);
};
